import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FormInput } from '../../FormComponents/Input/Input';
import { CurrentBody, DynamicInputTable, InputBody, InputHeader, InputTable } from '../../FormComponents/Input/DynamicInputTable';
import { Dropdown } from '../../FormComponents/Dropdown';
import DatePicker from '../../Fundamentals/DatePicker';
import Spinner from '../../Fundamentals/Spinner/Spinner';

import routes from '../../../Constants/routes';
import helpers from '../../../Utils/helpers';

export function TerminationReasonForm({terminationReasonForm, terminationReasonsActions, isEdit, commonActions, salesChannels }) {
    const [subReasonForm, setSubReasonForm] = useState({ name: '', externalId: null });
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [isLoadingSalesChannels, setIsLoadingSalesChannels] = useState(false);
    const [terminationReasonSalesChannels, setTerminationReasonSalesChannels] = useState([]);
    const [salesChannelForm, setSalesChannelForm] = useState({
        selectedChannel: null,
        validFrom: undefined,
        validTo: undefined
    });

    useEffect(() => {
        if (isEdit && terminationReasonForm?.data?.id) {
            setIsLoadingSalesChannels(true);

            terminationReasonsActions.getSalesChannelsForTerminationReason(terminationReasonForm.data.id, (salesChannels) => {
                const existingSalesChannels = salesChannels.map(sc => ({
                    id: sc.id,
                    name: sc.name,
                    description: sc.description,
                    validFrom: sc.validFrom,
                    validTo: sc.validTo,
                }));

                setTerminationReasonSalesChannels(existingSalesChannels);
                setIsLoadingSalesChannels(false);
            });
        }
    }, []);

    const onChange = (value, key) => {
        commonActions.changeMultiFormField('terminationReasonForm', key, value);
    };

    const onSave = () => {
        const salesChannels = terminationReasonSalesChannels.map(channel => ({
            id: channel.id,
            validFrom: channel.validFrom,
            validTo: channel.validTo
        }));
        terminationReasonForm.data.salesChannels = salesChannels;      

        const action = isEdit ? terminationReasonsActions.updateTerminationReason : terminationReasonsActions.createTerminationReason;

        action(terminationReasonForm, () => navigate(routes.icaTerminationReasons));
    };

    const onAddSubreason = () => {
        if (!subReasonForm.name) return;
        const newSubReason = { name: subReasonForm.name, externalId: subReasonForm.externalId };
        if (isEdit) {
            terminationReasonsActions.createTerminationReason({ data: { ...newSubReason, parentId: terminationReasonForm.data.id } }, () =>
                terminationReasonsActions.getTerminationReasonForEdit(terminationReasonForm.data.id)
            );
        } else {
            onChange([...terminationReasonForm.data.subReasons, newSubReason], 'subReasons');
        }

        setSubReasonForm({ name: '', externalId: null });
    };

    const onDeleteSubreason = (index) => {
        const updatedSubReasons = terminationReasonForm.data.subReasons.filter((_, i) => i !== index);
        if (isEdit) {
            terminationReasonsActions.deleteSubTerminationReason(terminationReasonForm.data.subReasons[index].id, terminationReasonForm.data.id, () =>
                terminationReasonsActions.getTerminationReasonForEdit(terminationReasonForm.data.id)
            );
        } else {
            onChange(updatedSubReasons, 'subReasons');
        }
    };

    const onAddSalesChannel = () => {
        if (!salesChannelForm.selectedChannel) return;

        const isAlreadyAdded = terminationReasonSalesChannels.some(
            (channel) => channel.selectedChannel?.id === salesChannelForm.selectedChannel?.id
        );
        if (isAlreadyAdded) return;

        const newSalesChannel = {
            id: salesChannelForm.selectedChannel.id,
            name: salesChannelForm.selectedChannel.name,
            description: salesChannelForm.selectedChannel.description,
            validFrom: salesChannelForm.validFrom,
            validTo: salesChannelForm.validTo
        };

        setTerminationReasonSalesChannels([...terminationReasonSalesChannels, newSalesChannel]);

        setSalesChannelForm({
            selectedChannel: null,
            validFrom: undefined,
            validTo: undefined
        });
    };

    const onDeleteSalesChannel = (index) => {
        const updatedChannels = terminationReasonSalesChannels.filter((_, i) => i !== index);
        setTerminationReasonSalesChannels(updatedChannels);
    };

    const onSalesChannelChange = (selectedChannelId) => {
        const selectedChannel = salesChannels.find((channel) => channel.id === selectedChannelId);
        
        setSalesChannelForm({
            ...salesChannelForm,
            selectedChannel: selectedChannel
        });
    };

    const getAvailableSalesChannels = () => {
        return salesChannels.filter(sc =>
            !terminationReasonSalesChannels.some(tsc => tsc.id === sc.id)
        );
    };

    const headRows = (
        <>
            <th className="required" style={{ width: "45%" }}>{t('terminationreasonsform.subreason')}</th>
            <th style={{ width: "45%" }}>{t('terminationreasonsform.externalid')}</th>
            <th></th>
        </>
    );

    return (
        <>
            <div className="row">
                <div className="col-lg-4 px-0">
                    <fieldset className='mb-24'>
                        <legend className="bold">{t('terminationreasonsform.legendDetails')}</legend>
                        <div className='form-group'>
                            <FormInput label={t('common.name')} value={terminationReasonForm.data.name} onChange={(value) => onChange(value, 'name')} required />
                        </div>
                        <div className='form-group'>
                            <FormInput label={t('common.externalid')} value={terminationReasonForm.data.externalId} onChange={(value) => onChange(value || null, 'externalId')} />
                        </div>
                    </fieldset>
                </div>
            </div>
            <div className="row">
                <div className="col-lg-12 px-0">
                    <fieldset className='mb-32'>
                        <legend className="bold">{t('terminationreasonsform.legendSubReasons')}</legend>
                        <div>
                            <DynamicInputTable>
                                <InputTable disableAdd={!subReasonForm.name}>
                                    <InputHeader>{headRows}</InputHeader>
                                        <InputBody>
                                            <td className="p-12">
                                                <FormInput
                                                    value={subReasonForm.name}
                                                    placeholder={t('terminationreasonsform.subreason')}
                                                    onChange={(value) => setSubReasonForm({ ...subReasonForm, name: value })}
                                                />
                                            </td>
                                            <td className="p-12">
                                                <FormInput
                                                    value={subReasonForm.externalId}
                                                    placeholder={t('common.externalid')}
                                                    onChange={(value) => setSubReasonForm({ ...subReasonForm, externalId: value || null })}
                                                />
                                            </td>

                                            <td className="py-12 text-right">
                                                <button type="button" className="btn btn-primary btn-sm" disabled={!subReasonForm.name} onClick={onAddSubreason} style={{minWidth:'80px'}}>
                                                    {t('common.add')}
                                                </button>
                                            </td>
                                        </InputBody>
                                        <CurrentBody current={terminationReasonForm.data.subReasons} onDelete={onDeleteSubreason}>
                                            {(subReason) => (
                                                <>
                                                    <td>{subReason.name}</td>
                                                    <td>{subReason.externalId}</td>
                                                </>
                                            )}
                                        </CurrentBody>
                                    </InputTable>
                                </DynamicInputTable>
                            </div>
                    </fieldset>
                    <fieldset>
                        <legend className="bold">{t('terminationreasonsform.legendSalesChannels')}</legend>
                        <DynamicInputTable>
                            <InputTable>
                                <InputHeader>
                                    <th className='required'>{t('common.name')}</th>
                                    <th>{t('common.startdate')}</th>
                                    <th>{t('common.enddate')}</th>
                                    <th></th>
                                </InputHeader>
                                <InputBody>
                                    <td className="py-12">
                                        <Dropdown
                                            options={getAvailableSalesChannels()}
                                            initialValue={salesChannelForm.selectedChannel?.id}
                                            value={salesChannelForm.selectedChannel?.id}
                                            onChange={onSalesChannelChange}
                                            property="name"
                                            valueProperty="id"
                                            placeholder={t('terminationreasonsform.salesChannelDropdownPlaceholder')}
                                        />
                                    </td>
                                    <td className="py-12" style={{minWidth: '25%'}}>
                                        <DatePicker dateFormat={"YYYY-MM-DD"} showTime={false} onChange={(value) => setSalesChannelForm({ ...salesChannelForm, validFrom: value })} value={salesChannelForm.validFrom} closeOnSelect={false} />
                                    </td>
                                    <td className="py-12" style={{minWidth: '25%'}}>
                                        <DatePicker dateFormat={"YYYY-MM-DD"} showTime={false} onChange={(value) => setSalesChannelForm({ ...salesChannelForm, validTo: value })} value={salesChannelForm.validTo} closeOnSelect={false} />
                                    </td>
                                    <td className="py-12 text-right">
                                        <button type="button" className="btn btn-primary btn-sm" style={{minWidth:'80px'}} disabled={!salesChannelForm.selectedChannel} onClick={onAddSalesChannel}>
                                            {t('common.add')}
                                        </button>
                                    </td>
                                </InputBody>

                                {(terminationReasonSalesChannels.length === 0 || isLoadingSalesChannels) && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={4} className="pt-12 pb-24">
                                                {isLoadingSalesChannels ? <Spinner /> : t('terminationreasonsform.noSalesChannels')}
                                            </td>
                                        </tr>
                                    </tbody>)
                                }

                                <CurrentBody current={terminationReasonSalesChannels} onDelete={onDeleteSalesChannel}>
                                    {(salesChannel) => (
                                        <>
                                            <td>{salesChannel.name}</td>
                                            <td>{salesChannel.validFrom ? helpers.formatDate(salesChannel.validFrom) : '-'}</td>
                                            <td>{salesChannel.validTo ? helpers.formatDate(salesChannel.validTo) : '-'}</td>
                                        </>
                                    )}
                                </CurrentBody>
                            </InputTable>
                        </DynamicInputTable>
                    </fieldset>
                </div>
            </div>
            <div className="mt-24 d-flex justify-content-end gap-8 align-items-center flex-wrap">
                <button className="btn btn-primary btn-sm" onClick={onSave}>
                    {t('common.save')}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate(routes.icaTerminationReasons)}>
                    {t('common.cancel')}
                </button>
            </div>
        </>
    );
}

